import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
} from '@/components/ui/InfoCard.tsx';
import MetricList, { MetricListRow } from '@/components/ui/MetricList.tsx';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import { largestSegment } from '@/components/charts/breakdownAdapter.ts';
import { BreakdownDto, MoneyBreakdownDto } from '@/types/stats/BreakdownDto.ts';
import {
  findMetric,
  formatMetricValue,
  sortByCurrency,
} from '@/types/stats/metric.utils.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import {
  OptionStatsMetricKey,
  optionStatsMetricLabel,
} from '@/features/dashboard/tours/types/sales/optionStatsMetrics.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';
import { pluralize } from '@/utils/pluralize.ts';

interface OptionStatsCardProps {
  /** `total` to liczba uczestników; segmenty `LABELED` per opcja. */
  selections: BreakdownDto;
  revenue: MoneyBreakdownDto[];
}

/** Ile opcje dodatkowe wnoszą do wyjazdu — w ludziach i w pieniądzach. */
const OptionStatsCard = ({ selections, revenue }: OptionStatsCardProps) => {
  const currencies = sortByCurrency(revenue);
  // Waluta w etykiecie tylko wtedy, gdy jest co odróżniać — przy jednej
  // walucie dopisek byłby szumem.
  const withCurrency = (label: string, currency: string) =>
    currencies.length > 1 ? `${label} · ${getCurrencyLabel(currency)}` : label;

  const withOptions = findMetric(
    selections.metrics,
    OptionStatsMetricKey.PARTICIPANTS_WITH_OPTIONS,
  );
  const top = largestSegment(selections);

  const rows: MetricListRow[] = [
    withOptions
      ? {
          id: 'participants-with-options',
          label: optionStatsMetricLabel(
            OptionStatsMetricKey.PARTICIPANTS_WITH_OPTIONS,
          ),
          note: `z ${pluralize(selections.total, 'uczestnika', 'uczestników', 'uczestników')}`,
          value: formatMetricValue(withOptions),
        }
      : null,
    ...currencies.map((entry) => {
      const share = findMetric(
        entry.metrics,
        OptionStatsMetricKey.REVENUE_SHARE,
        entry.currency,
      );

      return {
        id: `revenue-${entry.currency}`,
        label: withCurrency('Przychód z opcji', entry.currency),
        value: formatPrice(entry.total, entry.currency),
        delta: share
          ? `${formatMetricValue(share)} przychodu wyjazdu`
          : undefined,
      };
    }),
    ...currencies.map((entry) => {
      const perParticipant = findMetric(
        entry.metrics,
        OptionStatsMetricKey.REVENUE_PER_PARTICIPANT,
        entry.currency,
      );

      return perParticipant
        ? {
            id: `revenue-per-participant-${entry.currency}`,
            label: withCurrency(
              optionStatsMetricLabel(
                OptionStatsMetricKey.REVENUE_PER_PARTICIPANT,
              ),
              entry.currency,
            ),
            value: formatMetricValue(perParticipant),
          }
        : null;
    }),
    {
      id: 'most-popular',
      label: 'Najczęściej dokupywane',
      note: top?.label,
      value: top ? top.value : <EmptyValue />,
    },
  ].filter((entry) => entry !== null);

  return (
    <InfoCard>
      <InfoCardHeader title="Opcje dodatkowe" />
      <InfoCardBody className="py-1">
        <MetricList rows={rows} />
      </InfoCardBody>
    </InfoCard>
  );
};

export default OptionStatsCard;
